import { Component, ComponentFactory, ComponentFactoryResolver, Directive, ElementRef, EventEmitter, Injector, Input, OnChanges, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { Hsva, SliderDimension, SliderPosition } from './classes';
import { ColorPickerService } from './color-picker.service';

@Directive({
    selector: '[colorPicker]',
    host: {
        '(click)': 'onClick()'
    }
})
export class ColorPickerDirective implements OnInit, OnChanges {
    @Output('colorPickerChange') colorPickerChange = new EventEmitter<string>(true);
    @Output('cpOKButtonFunction') cpOKButtonFunction = new EventEmitter<string>(true);
    @Input('colorPicker') colorPicker: string;
    @Input('cpCancelButton') cpCancelButton = false;
    @Input('cpCancelButtonClass') cpCancelButtonClass = 'cp-cancel-button-class';
    @Input('cpCancelButtonText') cpCancelButtonText = 'Cancel';
    @Input('cpOKButton') cpOKButton = false;
    @Input('cpOKButtonClass') cpOKButtonClass = 'cp-ok-button-class';
    @Input('cpOKButtonText') cpOKButtonText = 'OK';
    @Input('cpSaveClickOutside') cpSaveClickOutside = true;

    private dialog: any;
    private created: boolean;
    private ignoreChanges = false;
	private readonly compFactory: ComponentFactory<DialogComponent>;

    constructor(
		private resolver: ComponentFactoryResolver,
		private injector: Injector,
		private vcRef: ViewContainerRef, 
		private el: ElementRef, 
		private service: ColorPickerService
	) {
		this.created = false;
		this.compFactory = resolver.resolveComponentFactory(DialogComponent);
    }

    ngOnChanges(changes: any): void {
        if (changes.colorPicker) {
            if (this.dialog && !this.ignoreChanges) {
                this.dialog.setColorFromString(changes.colorPicker.currentValue, false);
            }
            this.ignoreChanges = false;
        }
    }

    ngOnInit() {
        let hsva = this.service.stringToHsva(this.colorPicker);
        if (hsva === null) { hsva = this.service.stringToHsva(this.colorPicker); }
        this.colorPickerChange.emit(this.service.outputFormat(hsva));
    }

    onClick() {
        this.openDialog();
    }

    openDialog() {
        if (!this.created) {
			this.created = true;
			const cmpRef = this.vcRef.createComponent(this.compFactory, 0, this.injector, []);
			cmpRef.instance.setDialog(this, this.el, this.colorPicker,
				this.cpCancelButton,
				this.cpCancelButtonClass,
				this.cpCancelButtonText,
				this.cpOKButton,
				this.cpOKButtonClass,
				this.cpOKButtonFunction,
				this.cpOKButtonText,
				this.cpSaveClickOutside
			);
			this.dialog = cmpRef.instance;
        } else if (this.dialog) {
            this.dialog.openDialog(this.colorPicker);
        }
    }

    colorChanged(value: string, ignore: boolean = true) {
        this.ignoreChanges = ignore;
        this.colorPickerChange.emit(value);
    }
}


@Directive({
    selector: '[text]',
    host: {
        '(input)': 'changeInput($event.target.value)'
    }
})
export class TextDirective {
    @Output('newValue') newValue = new EventEmitter<any>();
    @Input('text') text: any;
    @Input('rg') rg: number;

    changeInput(value: string) {
        const numeric = parseFloat(value);
        if (!isNaN(numeric) && numeric >= 0 && numeric <= this.rg) {
            this.newValue.emit({ v: numeric, rg: this.rg });
        }
    }
}

@Directive({
    selector: '[slider]',
    host: {
        '(mousedown)': 'start($event)',
        '(touchstart)': 'start($event)'
    }
})
export class SliderDirective {
    @Output('newValue') newValue = new EventEmitter<any>();
    @Input('slider') slider: string;
    @Input('rgX') rgX: number;
    @Input('rgY') rgY: number;
    private listenerMove: any;
    private listenerStop: any;

    constructor(private el: ElementRef) {
        this.listenerMove = (event: any) => { this.move(event); };
        this.listenerStop = () => { this.stop(); };
    }

    setCursor(event: any) {
        const height = this.el.nativeElement.offsetHeight;
        const width = this.el.nativeElement.offsetWidth;
        const x = Math.max(0, Math.min(this.getX(event), width));
        const y = Math.max(0, Math.min(this.getY(event), height));

        if (this.rgX !== undefined && this.rgY !== undefined) {
            this.newValue.emit({ s: x / width, v: (1 - y / height), rgX: this.rgX, rgY: this.rgY });
        } else if (this.rgX === undefined && this.rgY !== undefined) {
            this.newValue.emit({ v: y / height, rg: this.rgY });
        } else {
            this.newValue.emit({ v: x / width, rg: this.rgX });
        }
    }

    move(event: any) {
        event.preventDefault();
        this.setCursor(event);
    }

    start(event: any) {
        this.setCursor(event);
        document.addEventListener('mousemove', this.listenerMove);
        document.addEventListener('touchmove', this.listenerMove);
        document.addEventListener('mouseup', this.listenerStop);
        document.addEventListener('touchend', this.listenerStop);
    }

    stop() {
        document.removeEventListener('mousemove', this.listenerMove);
        document.removeEventListener('touchmove', this.listenerMove);
        document.removeEventListener('mouseup', this.listenerStop);
        document.removeEventListener('touchend', this.listenerStop);
    }

    getX(event: any): number {
        return (event.pageX !== undefined ? event.pageX :
            event.touches[0].pageX) - this.el.nativeElement.getBoundingClientRect().left - window.pageXOffset;
    }
    getY(event: any): number {
        return (event.pageY !== undefined ? event.pageY :
            event.touches[0].pageY) - this.el.nativeElement.getBoundingClientRect().top - window.pageYOffset;
    }
}

@Component({
    selector: 'color-picker',
    templateUrl: './color-picker.html',
    styleUrls: ['./color-picker.css']
})
export class DialogComponent implements OnInit {
    private hsva: Hsva;
    public hexText: string;
    private outputColor: string;
    public selectedColor: string;
    public hueSliderColor: string;
    public slider: SliderPosition;
    private sliderDimMax: SliderDimension;
    public show: boolean;
    public top: number;
    public left: number;
    private position: string;
    private directiveInstance: any;
    private initialColor: string;
    private directiveElementRef: ElementRef;

    private listenerMouseDown: any;
    private listenerResize: any;

    public cpCancelButton: boolean;
    public cpCancelButtonClass: string;
    public cpCancelButtonText: string;
    public cpOKButton: boolean;
    public cpOKButtonClass: string;
    public cpOKButtonText: string;
    public cpSaveClickOutside: boolean;

    private dialogArrowSize = 10;
    private dialogArrowOffset = 15;
    public arrowTop: number;

    @Output('cpOKButtonFunction') cpOKButtonFunction = new EventEmitter<string>(true);
    @ViewChild('hueSlider') hueSlider: any;
    @ViewChild('dialogPopup') dialogElement: any;

    constructor(private el: ElementRef, private service: ColorPickerService) { }

    setDialog(instance: any,
        elementRef: ElementRef,
        color: any,
        cpCancelButton: boolean,
        cpCancelButtonClass: string,
        cpCancelButtonText: string,
        cpOKButton: boolean,
        cpOKButtonClass: string,
        cpOKButtonFunction: EventEmitter<string>,
        cpOKButtonText: string,
        cpSaveClickOutside: boolean
    ) {
        this.directiveInstance = instance;
        this.initialColor = color;
        this.directiveElementRef = elementRef;
        this.cpCancelButton = cpCancelButton;
        this.cpCancelButtonClass = cpCancelButtonClass;
        this.cpCancelButtonText = cpCancelButtonText;
        this.cpOKButton = cpOKButton;
        this.cpOKButtonClass = cpOKButtonClass;
        this.cpOKButtonFunction = cpOKButtonFunction;
        this.cpOKButtonText = cpOKButtonText;
        this.cpSaveClickOutside = cpSaveClickOutside;
    }

    ngOnInit() {
        const hueWidth = this.hueSlider.nativeElement.offsetWidth;
        this.sliderDimMax = new SliderDimension(hueWidth, 230, 130, 0);
        this.slider = new SliderPosition(0, 0, 0, 0);
        this.listenerMouseDown = (event: any) => { this.onMouseDown(event); };
        this.openDialog(this.initialColor, false);
    }

    setInitialColor(color: any) {
        this.initialColor = color;
    }

    openDialog(color: any, emit: boolean = true) {
        this.setInitialColor(color);
        this.setColorFromString(color, emit);
        this.openColorPicker();
    }

    cancelColor() {
        this.setColorFromString(this.initialColor, true);
        this.directiveInstance.colorChanged(this.initialColor, true);
        this.closeColorPicker();
    }

    oKColor($event) {
        this.cpOKButtonFunction.emit($event);
        this.closeColorPicker();
    }

    setColorFromString(value: string, emit: boolean = true) {
        let hsva: Hsva;
        hsva = this.service.stringToHsva(value);
        if (hsva) {
            this.hsva = hsva;
            this.update(emit);
        }
    }

    onMouseDown(event: any) {
        if ((!this.isDescendant(this.el.nativeElement, event.target)
            && event.target !== this.directiveElementRef.nativeElement)) {
            if (!this.cpSaveClickOutside) {
                this.setColorFromString(this.initialColor, false);
                this.directiveInstance.colorChanged(this.initialColor);
            }
            this.closeColorPicker();
        }
    }

    openColorPicker() {
        if (!this.show) {
            this.setDialogPosition();
            this.show = true;
            document.addEventListener('mousedown', this.listenerMouseDown);
            window.addEventListener('resize', this.listenerResize);
        }
    }

    closeColorPicker() {
        if (this.show) {
            this.show = false;
            document.removeEventListener('mousedown', this.listenerMouseDown);
            window.removeEventListener('resize', this.listenerResize);
        }
    }

    setDialogPosition() {
        const dialogHeight = this.dialogElement.nativeElement.offsetHeight;
        let node = this.directiveElementRef.nativeElement, position = 'static';
        let parentNode: any = null;
        while (node !== null && node.tagName !== 'HTML') {
            position = window.getComputedStyle(node).getPropertyValue('position');
            if (position !== 'static' && parentNode === null) {
                parentNode = node;
            }
            node = node.parentNode;
        }
        const boxDirective = this.createBox(this.directiveElementRef.nativeElement, true);
        if (parentNode === null) { parentNode = node; }
        const boxParent = this.createBox(parentNode, true);
        this.top = boxDirective.top - boxParent.top - 5;
        this.left = boxDirective.left - boxParent.left;

        this.top += 20 - this.dialogArrowOffset;
        this.left += boxDirective.width + this.dialogArrowSize + 170;
    }

    setHue(val: { v: number, rg: number }) {
        this.hsva.h = val.v / val.rg;
        this.update();
    }

    setSaturationAndBrightness(val: { s: number, v: number, rgX: number, rgY: number }) {
        this.hsva.s = val.s / val.rgX;
        this.hsva.v = val.v / val.rgY;
        this.update();
    }

    update(emit: boolean = true) {
        const rgba = this.service.denormalizeRGBA(this.service.hsvaToRgba(this.hsva));
        const hueRgba = this.service.denormalizeRGBA(this.service.hsvaToRgba(new Hsva(this.hsva.h, 1, 1, 1)));
        this.hexText = this.service.hexText(rgba);
        this.hueSliderColor = 'rgb(' + hueRgba.r + ',' + hueRgba.g + ',' + hueRgba.b + ')';
        const lastOutput = this.outputColor;
        this.outputColor = this.selectedColor = this.service.outputFormat(this.hsva);
        this.slider = new SliderPosition((this.hsva.h) * this.sliderDimMax.h - 8, this.hsva.s * this.sliderDimMax.s - 8,
            (1 - this.hsva.v) * this.sliderDimMax.v - 8, this.hsva.a * this.sliderDimMax.a - 8);
        if (emit && lastOutput !== this.outputColor) {
            this.directiveInstance.colorChanged(this.outputColor);
        }
    }

    isDescendant(parent: any, child: any): boolean {
        let node: any = child.parentNode;
        while (node !== null) {
            if (node === parent) {
                return true;
            }
            node = node.parentNode;
        }
        return false;
    }

    createBox(element: any, offset: boolean): any {
        return {
            top: element.getBoundingClientRect().top + (offset ? window.pageYOffset : 0),
            left: element.getBoundingClientRect().left + (offset ? window.pageXOffset : 0),
            width: element.offsetWidth,
            height: element.offsetHeight
        };
    }
}



// WEBPACK FOOTER //
// ./src/app/components/color-picker/color-picker.directive.ts